import React, { useEffect, useState } from 'react';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';

interface Organization {
  id: string;
  name: string;
}

interface JobTemplate {
  id: string;
  organization_id: string;
  position: string;
  employment_type: string;
  department?: string;
  level?: string;
  job_requirements?: string;
  sample_questions?: string;
  competency_focus?: Record<string, any>;
  competency_weights?: Record<string, number>;
  created_at: string;
  updated_at: string;
}

interface JobTemplateForm {
  organization_id: string;
  position: string;
  employment_type: string;
  department: string;
  level: string;
  job_requirements: string;
  sample_questions: string;
}

export const JobTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<JobTemplate[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [viewingTemplate, setViewingTemplate] = useState<JobTemplate | null>(null);
  const [form, setForm] = useState<JobTemplateForm>({
    organization_id: '',
    position: '',
    employment_type: 'Full-time',
    department: '',
    level: '',
    job_requirements: '',
    sample_questions: ''
  });
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [selectedOrg, setSelectedOrg] = useState<string>('');

  useEffect(() => {
    Promise.all([fetchJobTemplates(), fetchOrganizations()]);
  }, [selectedOrg]);

  const fetchJobTemplates = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const url = selectedOrg 
        ? `${import.meta.env.VITE_API_BASE_URL}/api/admin/job-templates?organization_id=${selectedOrg}`
        : `${import.meta.env.VITE_API_BASE_URL}/api/admin/job-templates`;
        
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTemplates(data);
      } else {
        setError('Failed to fetch job templates');
      }
    } catch (error) {
      setError('Network error while fetching job templates');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/job-templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });

      if (response.ok) {
        setSuccess('Job template created successfully');
        setIsModalOpen(false);
        setForm({
          organization_id: '',
          position: '',
          employment_type: 'Full-time',
          department: '',
          level: '',
          job_requirements: '',
          sample_questions: ''
        });
        fetchJobTemplates();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to create job template');
      }
    } catch (error) {
      setError('Network error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this job template?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}/api/admin/job-templates/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setSuccess('Job template deleted successfully');
        fetchJobTemplates();
      } else {
        setError('Failed to delete job template');
      }
    } catch (error) {
      setError('Network error while deleting job template');
    }
  };

  const handleView = (template: JobTemplate) => {
    setViewingTemplate(template);
    setIsViewModalOpen(true);
  };

  const openCreateModal = () => {
    setForm({
      organization_id: '',
      position: '',
      employment_type: 'Full-time',
      department: '',
      level: '',
      job_requirements: '',
      sample_questions: ''
    });
    setIsModalOpen(true);
  };

  const getOrgName = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : orgId;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Job Templates</h1>
          <p className="text-gray-600 mt-2">Create role-specific interview templates</p>
        </div>
        <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700">
          Create Template
        </Button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}

      {/* Filter */}
      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg p-4 border border-gray-200">
        <div className="flex items-center space-x-4">
          <label htmlFor="orgFilter" className="text-sm font-medium text-gray-700">
            Filter by Organization:
          </label>
          <select
            id="orgFilter"
            value={selectedOrg}
            onChange={(e) => setSelectedOrg(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Organizations</option>
            {organizations.map(org => (
              <option key={org.id} value={org.id}>{org.name} ({org.id})</option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white/80 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Job Templates {selectedOrg && `for ${getOrgName(selectedOrg)}`}
          </h2>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Position
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employment Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Department/Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-gray-50/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{getOrgName(template.organization_id)}</div>
                      <div className="text-xs text-gray-500">{template.organization_id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{template.position}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {template.employment_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {template.department && <div>Dept: {template.department}</div>}
                      {template.level && <div>Level: {template.level}</div>}
                      {!template.department && !template.level && <div className="text-gray-400">N/A</div>}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600">
                      {new Date(template.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm space-x-2">
                    <button
                      onClick={() => handleView(template)}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(template.id)}
                      className="text-red-600 hover:text-red-800 font-medium"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {templates.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-400 text-lg mb-2">📋</div>
              <p className="text-gray-500">No job templates found</p>
              <Button onClick={openCreateModal} className="mt-4 bg-blue-600 hover:bg-blue-700">
                Create your first template
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Create Job Template"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="organization_id" className="block text-sm font-medium text-gray-700 mb-2">
              Organization
            </label>
            <select
              id="organization_id"
              value={form.organization_id}
              onChange={(e) => setForm(prev => ({ ...prev, organization_id: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              <option value="">Select Organization</option>
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name} ({org.id})</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                Position
              </label>
              <input
                type="text"
                id="position"
                value={form.position}
                onChange={(e) => setForm(prev => ({ ...prev, position: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Software Engineer"
                required
              />
            </div>

            <div>
              <label htmlFor="employment_type" className="block text-sm font-medium text-gray-700 mb-2">
                Employment Type
              </label>
              <select
                id="employment_type"
                value={form.employment_type}
                onChange={(e) => setForm(prev => ({ ...prev, employment_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Full-time">Full-time</option>
                <option value="Part-time">Part-time</option>
                <option value="Contract">Contract</option>
                <option value="Internship">Internship</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                Department (Optional)
              </label>
              <input
                type="text"
                id="department"
                value={form.department}
                onChange={(e) => setForm(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Engineering"
              />
            </div>

            <div>
              <label htmlFor="level" className="block text-sm font-medium text-gray-700 mb-2">
                Level (Optional)
              </label>
              <input
                type="text"
                id="level"
                value={form.level}
                onChange={(e) => setForm(prev => ({ ...prev, level: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Senior, Mid-level"
              />
            </div>
          </div>

          <div>
            <label htmlFor="job_requirements" className="block text-sm font-medium text-gray-700 mb-2">
              Job Requirements
            </label>
            <textarea
              id="job_requirements"
              value={form.job_requirements}
              onChange={(e) => setForm(prev => ({ ...prev, job_requirements: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
              placeholder="Describe the key requirements and skills needed for this role..."
            />
          </div>

          <div>
            <label htmlFor="sample_questions" className="block text-sm font-medium text-gray-700 mb-2">
              Sample Questions
            </label>
            <textarea
              id="sample_questions"
              value={form.sample_questions}
              onChange={(e) => setForm(prev => ({ ...prev, sample_questions: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent h-24"
              placeholder="Provide sample questions that should be asked for this role..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="bg-gray-500 hover:bg-gray-600"
            >
              Cancel
            </Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
              Create Template
            </Button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        title={`Job Template: ${viewingTemplate?.position}`}
      >
        {viewingTemplate && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-700">Organization</h4>
                <p className="text-sm text-gray-900">{getOrgName(viewingTemplate.organization_id)}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700">Employment Type</h4>
                <p className="text-sm text-gray-900">{viewingTemplate.employment_type}</p>
              </div>
              {viewingTemplate.department && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Department</h4>
                  <p className="text-sm text-gray-900">{viewingTemplate.department}</p>
                </div>
              )}
              {viewingTemplate.level && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700">Level</h4>
                  <p className="text-sm text-gray-900">{viewingTemplate.level}</p>
                </div>
              )}
            </div>

            {viewingTemplate.job_requirements && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Job Requirements</h4>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{viewingTemplate.job_requirements}</p>
                </div>
              </div>
            )}

            {viewingTemplate.sample_questions && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Questions</h4>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-900 whitespace-pre-wrap">{viewingTemplate.sample_questions}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-xs text-gray-500">
              <div>
                <strong>Created:</strong> {new Date(viewingTemplate.created_at).toLocaleString()}
              </div>
              <div>
                <strong>Updated:</strong> {new Date(viewingTemplate.updated_at).toLocaleString()}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};